import { useDispatch } from 'react-redux'
import { matchPath, useLocation } from 'react-router-dom'
import { getFulfilmentCenterText } from '../../helpers'
import { useAppSelector } from '../../hooks'
import { useModalControl } from '../../hooks/useModalControl'
import {
  signOut,
  updatePOSCreator,
  updatePOSFulfilmentCenter,
  updateSelectedFulfilmentCenter,
} from '../../redux-toolkits/auth/auth.reducer'
import { FulfilmentCentreRole } from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { Avatar } from '../avatar'
import { Button } from '../button'
import Icon from '../icon'
import Dropdown from '../inputs/dropdown'
import { Logo } from '../logo'
import { SelectFulfilmentCenterModal } from '../modal/app_modals/selectFulfilmentCenter'

const allowCenterSelectorPaths = [
  { path: '/dashboard', maxSelection: undefined },
  {
    path: '/orders',
    maxSelection: undefined,
  },
  { path: '/orders/:harmonizedReference', maxSelection: undefined },
  { path: '/creators', maxSelection: undefined },
  { path: '/creators/:id', maxSelection: undefined },
  { path: '/products', maxSelection: undefined },
  { path: '/pos', maxSelection: 1 },
  { path: '/settlement', maxSelection: undefined },
  { path: '/transactions', maxSelection: undefined },
]

const TopNavigation = ({
  navOpen,
  setNavOpen,
}: {
  navOpen?: boolean
  setNavOpen?: (val: boolean) => void
}): JSX.Element => {
  const dispatch = useDispatch()

  const { pathname } = useLocation()

  const handleLogout = (): void => {
    dispatch(signOut())
  }

  const { user, selectedFulfilmentCenters, POSFulfilmentCenter } =
    useAppSelector((state) => state.auth)

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  return (
    <>
      <div
        className="
        px-4 md:px-10 py-5
        flex justify-between items-center gap-6
        border-b !z-[999] w-full
        "
      >
        <div className="flex items-center gap-6">
          {user?.firstname && user.lastname && (
            <button
              onClick={() => {
                setNavOpen && setNavOpen(!navOpen)
              }}
            >
              <Icon
                id={navOpen ? 'close-icon' : 'menu-icon'}
                width={navOpen ? 20 : 30}
                height={navOpen ? 20 : 30}
                className="transition-all duration-300 flex xl:hidden"
              />
            </button>
          )}

          <Logo />
        </div>

        {allowCenterSelectorPaths.some((pattern) =>
          matchPath(pattern, pathname),
        ) ? (
          <Button
            type="button"
            label={getFulfilmentCenterText(
              pathname === '/pos'
                ? [POSFulfilmentCenter].filter((c) => c !== undefined)
                : selectedFulfilmentCenters,
            )}
            onClick={() => {
              handleModalOpen('selectFulfilmentCenterModal')
            }}
            icon="caret-down"
            iconPosition="right"
            variant="border-grey"
            primary={false}
            className="ml-auto max-md:!hidden !shadow-sm"
            labelClassName="max-w-[150px] whitespace-nowrap !text-ellipsis !line-clamp-1"
          />
        ) : undefined}

        {user?.firstname && user.lastname ? (
          <div
            className="
            flex items-center justify-between
            text-black gap-6
            "
          >
            <Icon id="notification-icon" height={20} width={20} />

            <Dropdown
              menuButton={
                <Avatar name={`${user?.firstname} ${user?.lastname}`} />
              }
              menuItems={[
                {
                  name: (
                    <div>
                      <p className="text-base font-recoleta font-medium">{`${user?.firstname} ${user?.lastname}`}</p>
                      <p className="text-xs text-sec-black flex justify-between items-center">
                        {user?.email}
                      </p>
                    </div>
                  ),
                  value: '',
                },
                {
                  name: (
                    <div className="text-sm text-sec-black flex items-center gap-2">
                      <Icon id="warehouse" width={24} height={24} />
                      <p className="text-xs">
                        {getFulfilmentCenterText(
                          pathname === '/pos'
                            ? [POSFulfilmentCenter].filter(
                                (c) => c !== undefined,
                              )
                            : selectedFulfilmentCenters,
                        )}
                      </p>
                    </div>
                  ),
                  value: '',
                  onClick: () => {
                    handleModalOpen('selectFulfilmentCenterModal')
                  },
                  hidden: false,
                },
                {
                  name: (
                    <div className="text-danger flex items-center gap-2 w-full">
                      <Icon id="logout2-icon" height={24} width={24} />
                      <span>Sign out</span>
                    </div>
                  ),
                  value: '',
                  onClick: () => {
                    handleLogout()
                  },
                },
              ]}
              position="bottom end"
            />
          </div>
        ) : undefined}
      </div>

      <SelectFulfilmentCenterModal
        open={modalOpen && modal === 'selectFulfilmentCenterModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        maxSelection={
          allowCenterSelectorPaths.find((p) => p.path === pathname)
            ?.maxSelection
        }
        selectedCenters={
          pathname === '/pos'
            ? [POSFulfilmentCenter].filter((c) => c !== undefined)
            : selectedFulfilmentCenters
        }
        onSuccessCallback={(centers, resetForm) => {
          if (pathname === '/pos') {
            dispatch(updatePOSFulfilmentCenter(centers[0]))
            dispatch(updatePOSCreator(undefined))
          } else {
            const physicalCentres = centers.flatMap((centre) =>
              centre.role === FulfilmentCentreRole.PARENT
                ? (user?.fulfillmentCenter ?? []).filter(
                    (child) => child.role !== FulfilmentCentreRole.PARENT,
                  )
                : [centre],
            )
            dispatch(
              updateSelectedFulfilmentCenter(
                physicalCentres.filter(
                  (centre, index, all) =>
                    all.findIndex((item) => item._id === centre._id) === index,
                ),
              ),
            )
          }
          handleModalClose()
          resetForm()
        }}
        includeParentStorefronts={
          pathname === '/orders' || pathname.startsWith('/orders/')
        }
        useAssignedCentersOnly
      />
    </>
  )
}

export default TopNavigation
